import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // outline rounded doesn't exist?
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Popover, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { shuffle } from 'lodash'; // Importing the shuffle function from a library like Lodash
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AudioApi } from '../../api/AudioApi';
import { DialogPromptQuizQuestionAudio } from '../../api/DialogPromptApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import SpeakerBlueIcon from '../../asset/icon-speaker-blue.svg';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from '../TeacherAudioReportButton';
import { getAudioByText } from './DialogPromptComponent';


interface DialogPromptQuizQuestionAudioComponentProps {
    dialogPrompt: DialogPromptQuizQuestionAudio;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptQuizQuestionAudioComponent: React.FC<DialogPromptQuizQuestionAudioComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef, isSettings = false }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [componentList, setComponentList] = React.useState<any[]>([]);
    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [chatBubbleCorrectDone, setChatBubbleCorrectDone] = React.useState(false);

    const studentAvatar = localStorage.getItem('studentAvatar');
    const [vocabularyWordId, setVocabularyWordId] = React.useState<number | undefined>();

    // sometimes state not always up to date. reference always up to date.
    const vocabularyWordIdRef = React.useRef(vocabularyWordId);
    vocabularyWordIdRef.current = vocabularyWordId;

    const [answerList, setAnswerList] = React.useState<any[]>([]);

    const shuffledVocabWordListRef = React.useRef<any[]>([]);
    const [shuffledVocabWordList, setShuffledVocabWordList] = React.useState<any[]>([]);
    const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);
    const previewVocabularyWord = previewIndex !== null ? shuffledVocabWordList[previewIndex] ?? null : null;

    const touchStartX = React.useRef<number | null>(null);
    const touchEndX = React.useRef<number | null>(null);
    const minSwipeDistance = 50;
    const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.targetTouches[0].clientX; };
    const onTouchMove = (e: React.TouchEvent) => { touchEndX.current = e.targetTouches[0].clientX; };
    const onTouchEnd = () => {
        if (touchStartX.current === null || touchEndX.current === null) return;
        const distance = touchStartX.current - touchEndX.current;
        if (distance > minSwipeDistance) handleNext();
        if (distance < -minSwipeDistance) handlePrev();
        touchStartX.current = null;
        touchEndX.current = null;
    };
    const handlePrev = () => {
        setPreviewIndex(prev => {
            if (prev === null) return prev;
            return prev === 0 ? shuffledVocabWordList.length - 1 : prev - 1;
        });
    };
    const handleNext = () => {
        setPreviewIndex(prev => {
            if (prev === null) return prev;
            return prev === shuffledVocabWordList.length - 1 ? 0 : prev + 1;
        });
    };

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [teacherChinesePromptAudio, setTeacherChinesePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const teacherChinesePromptText = dialogPrompt.teacher_chinese_prompt?.trim() ?? '';
    const shouldUseTeacherChinesePrompt = teacherChinesePromptText.length > 0 && teacherChinesePromptText !== '<Answer/>';

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio, isSettings)
        if (shouldUseTeacherChinesePrompt) {
            getAudioByText(history, setErrorMessage, dialogPrompt.teacher_chinese_prompt, setTeacherChinesePromptAudio, isSettings)
        }
    }, [clearFooterList, dialogPrompt.id, dialogPrompt.prompt, dialogPrompt.yes_response, dialogPrompt.no_response, dialogPrompt.teacher_chinese_prompt, history, isSettings, setErrorMessage, shouldUseTeacherChinesePrompt]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [componentList, chatBubbleContainerRef])

    const [infoAnchorEl, setInfoAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedInfoText, setSelectedInfoText] = React.useState<string | null>(null);

    const handleInfoClick = (event: React.MouseEvent<HTMLElement>, wordInfoText: string) => {
        setInfoAnchorEl(event.currentTarget);
        setSelectedInfoText(wordInfoText);
    };

    const handleInfoClose = () => {
        setInfoAnchorEl(null);
        setSelectedInfoText(null);
    };

    const infoOpen = Boolean(infoAnchorEl);

    const handleSpeakerClick = React.useCallback((vocabularyWordId: number) => {
        const allWordList = [...dialogPrompt.incorrect_vocabulary_word_list];
        allWordList.push(dialogPrompt.correct_vocabulary_word);
        const vocabularyWord = allWordList.find(w => w.id == vocabularyWordId)

        if (vocabularyWord) {
            const audioPromise = AudioApi.getTeacherSentence(vocabularyWord.audio_sentence_id)

            audioPromise
                .then(response => {
                    // check if the response was successful
                    if (response.status >= 300) {
                        throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                    }
                    return response.data;
                })
                .then(blob => {
                    playAudio(blob)
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })

        } else {
            throw Error("Vocabulary word Id not found " + vocabularyWordId);
        }
    }, [dialogPrompt.incorrect_vocabulary_word_list, dialogPrompt.correct_vocabulary_word, playAudio, setErrorMessage, history, location]);

    const buildVocabularyAudioReportContext = React.useCallback((vocabularyWord: DialogPromptQuizQuestionAudio['correct_vocabulary_word']) => ({
        sentenceId: vocabularyWord.audio_sentence_id,
        dialogPromptId: dialogPrompt.id,
        textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
        audioText: vocabularyWord.audio_sentence || vocabularyWord.word,
        audioRole: 'quiz_question_audio_answer',
    }), [dialogPrompt.id]);

    const selectVocabularyWord = React.useCallback((selectedVocabularyWordId: number, replayIfAlreadySelected = false) => {
        const isAlreadySelected = vocabularyWordIdRef.current === selectedVocabularyWordId;
        if (!isAlreadySelected) {
            setVocabularyWordId(selectedVocabularyWordId);
            vocabularyWordIdRef.current = selectedVocabularyWordId;
        }
        if (!isAlreadySelected || replayIfAlreadySelected) {
            handleSpeakerClick(selectedVocabularyWordId);
        }
    }, [handleSpeakerClick]);

    const handleVocabularyWordChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        selectVocabularyWord(Number(event.target.value));
    }, [selectVocabularyWord]);

    // handleAnswerClick ref to break circular dep (renderPromptInputComponent passes handleAnswerClick to itself)
    const handleAnswerClickRef = React.useRef<() => void>(() => {});

    const renderPromptInputComponent = React.useCallback((
        vocabId: number | undefined,
        list: any[],
        onSubmit: () => void,   // 👈 pass a fresh submit function too!
    ) => (
        <RadioGroup
            key={vocabId ? vocabId : 'none-selected'}
            aria-label="options"
            name="options"
            value={vocabId}
            onChange={handleVocabularyWordChange}
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{ width: "100%", maxWidth: "100%", mx: "auto", gap: 2, flexWrap: "wrap" }}
            >
                {list}
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}   // 👈 fresh!
                >
                    Submit
                </Button>
            </Stack>
        </RadioGroup>
    ), [handleVocabularyWordChange]);

    const handleAnswerClick = React.useCallback(() => {

        if (vocabularyWordIdRef.current) {

            const allWordList = [...dialogPrompt.incorrect_vocabulary_word_list];
            allWordList.push(dialogPrompt.correct_vocabulary_word);
            const vocabularyWord = allWordList.find(w => w.id == vocabularyWordIdRef.current)!

            const foundAnswerChatBubbleCopy =
                <Stack direction="row" alignItems="center" justifyContent="right">
                    <div style={{ flex: 1, maxWidth: '300px' }}>
                        <Stack direction="row" alignItems="center" justifyContent="right" spacing={2}>
                            <TeacherAudioActions
                                context={buildVocabularyAudioReportContext(vocabularyWord)}
                                onPlay={() => { handleSpeakerClick(vocabularyWord.id) }}
                                speakerAriaLabel={`Play selected answer audio for ${vocabularyWord.word || vocabularyWord.audio_sentence || 'answer choice'}`}
                            />
                            <Stack direction="column" alignItems="center">
                                {vocabularyWord.word ?
                                    <Typography sx={{ fontFamily: 'KaiTi', fontSize: '24px', overflowWrap: 'anywhere', textAlign: 'center' }}>
                                        {vocabularyWord.word}
                                    </Typography>
                                    :
                                    <Typography sx={{ fontFamily: 'KaiTi', fontSize: '24px', overflowWrap: 'anywhere', textAlign: 'center' }}>
                                        {vocabularyWord.audio_sentence}
                                    </Typography>
                                }
                                {vocabularyWord.image_url ?
                                    <img
                                        src={vocabularyWord.image_url}
                                        alt={vocabularyWord.word}
                                        key={vocabularyWord.id}
                                        style={{ maxHeight: '125px', maxWidth: '125px', height: 'auto', width: 'auto' }}
                                    />
                                    :
                                    <>{/* create empty child so foundAnswer.props.children[0] works */}</>
                                }
                            </Stack>
                            {vocabularyWord.word_info_text && (<>
                                <IconButton onClick={(e) => handleInfoClick(e, vocabularyWord.word_info_text!)}>
                                    <InfoOutlinedIcon sx={{
                                        width: 30,          // bigger width
                                        height: 30,         // bigger height
                                        color: '#0062FF',
                                    }} />
                                </IconButton>
                            </>)}
                            <img
                                src={studentAvatar || ''}
                                style={{ maxHeight: '4em', maxWidth: '4em', marginRight: '16px' }}
                            />
                        </Stack>
                    </div>
                </Stack>

            const additionalComponentList: React.ReactNode[] = []
            additionalComponentList.push(foundAnswerChatBubbleCopy)

            const correct = vocabularyWordIdRef.current == dialogPrompt.correct_vocabulary_word.id
            if (correct) {
                clearFooterList()
                const newChatBubble = (
                    <ChatBubble
                        isTeacher={true}
                        message={dialogPrompt.yes_response}
                        messageAudio={yesResponseAudio ? yesResponseAudio : null}
                        showAvatar={showAvatar}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                        dialogPromptId={dialogPrompt.id}
                        audioRole="yes_response"
                        setChatBubbleDone={setChatBubbleCorrectDone}
                    />
                );
                setComponentList((prevComponentList) => [...prevComponentList, ...additionalComponentList, newChatBubble]);
                UserDialogPromptApi.addCorrectAnswer(dialogPrompt.id)
                    .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
            } else {
                setVocabularyWordId(undefined); // This will unselect all radio buttons
                vocabularyWordIdRef.current = undefined; // Reset ref to match the state

                const reshuffled = shuffle([...shuffledVocabWordListRef.current]);
                shuffledVocabWordListRef.current = reshuffled;
                setShuffledVocabWordList(reshuffled);
                const newAnswerList = reshuffled.map((vocabularyWordOption: any, index: number) => {
                    const optionId = "option" + vocabularyWordOption.id;
                    return (
                    <Box
                        component="label"
                        htmlFor={optionId}
                        key={vocabularyWordOption.id}
                        sx={{ display: "inline-flex", maxWidth: "100%", alignItems: "center", cursor: "pointer" }}
                        onClick={() => selectVocabularyWord(vocabularyWordOption.id)}
                    >
                        <Stack key={vocabularyWordOption.id} direction="row" alignItems="center">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Radio value={vocabularyWordOption.id} id={optionId} />
                                <img src={SpeakerBlueIcon} style={{ width: '36px', height: '36px', marginRight: '8px' }} onClick={() => handleSpeakerClick(vocabularyWordOption.id)} />
                            </Box>
                            <Stack direction="column" alignItems="center">
                                {vocabularyWordOption.word ? <Typography style={{ fontFamily: 'KaiTi', fontSize: '24px' }}>{vocabularyWordOption.word}</Typography> : <></>}
                                {vocabularyWordOption.image_url ? (
                                    <img
                                        src={vocabularyWordOption.image_url}
                                        alt={vocabularyWordOption.word}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setPreviewIndex(index);
                                        }}
                                        style={{ maxHeight: 125, maxWidth: 125, height: 'auto', width: 'auto', cursor: 'pointer', borderRadius: '12px', transition: 'transform 0.2s ease, filter 0.2s ease', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))' }}
                                        onMouseOver={(e) => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.08)'; (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 4px 16px rgba(0,0,0,0.12))'; }}
                                        onMouseOut={(e) => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))'; }}
                                    />
                                ) : <></>}
                            </Stack>
                        </Stack>
                    </Box>
                    );
                });
                setAnswerList(newAnswerList);
                setActionList([renderPromptInputComponent(undefined, newAnswerList, handleAnswerClickRef.current)])

                const newChatBubble = (
                    <ChatBubble
                        isTeacher={true}
                        message={dialogPrompt.no_response}
                        messageAudio={noResponseAudio ? noResponseAudio : null}
                        showAvatar={showAvatar}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                        dialogPromptId={dialogPrompt.id}
                        audioRole="no_response"
                    />
                );
                setComponentList((prevComponentList) => [...prevComponentList, ...additionalComponentList, newChatBubble]);
                UserDialogPromptApi.addIncorrectAnswer(dialogPrompt.id)
                    .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
            }
        }

    }, [
        dialogPrompt.incorrect_vocabulary_word_list, dialogPrompt.correct_vocabulary_word,
        dialogPrompt.yes_response, dialogPrompt.no_response, dialogPrompt.id,
        handleSpeakerClick, selectVocabularyWord, renderPromptInputComponent, buildVocabularyAudioReportContext,
        yesResponseAudio, noResponseAudio, showAvatar, chatBubbleContainerRef,
        clearFooterList, setActionList, setErrorMessage, history, location, studentAvatar,
    ]);

    handleAnswerClickRef.current = handleAnswerClick;

    React.useEffect(() => {
        if (chatBubbleDone) {
            const newComponent: any[] = [];
            if (dialogPrompt.image_src || dialogPrompt.image_src_url) {
                newComponent.push(<img
                    src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : undefined)}
                    alt={dialogPrompt.prompt}
                    style={{
                        maxHeight: '400px', maxWidth: '400px',
                        height: 'auto', width: 'auto',
                        margin: '0 auto', // Add this line to center the image horizontally
                        display: 'block', // Add this line to ensure the margin works correctly
                    }}
                />)
            }

            if (shouldUseTeacherChinesePrompt) {
                newComponent.push(<ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.teacher_chinese_prompt}
                    messageAudio={teacherChinesePromptAudio ? teacherChinesePromptAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    dialogPromptId={dialogPrompt.id}
                    audioRole="teacher_chinese_prompt"
                />);
            }
            setComponentList((prevComponentList) => [...prevComponentList, ...newComponent]);

            const allWordList = [...dialogPrompt.incorrect_vocabulary_word_list];
            allWordList.push(dialogPrompt.correct_vocabulary_word);
            const shuffledWordList = shuffle(allWordList);
            shuffledVocabWordListRef.current = shuffledWordList;
            setShuffledVocabWordList(shuffledWordList);

            const wordAnswerList = shuffledWordList.map((vocabularyWordOption, index) => {
                const optionId = "option" + vocabularyWordOption.id;
                return (
                <Box
                    component="label"
                    htmlFor={optionId}
                    key={vocabularyWordOption.id}
                    sx={{ display: "inline-flex", maxWidth: "100%", alignItems: "center", cursor: "pointer" }}
                    onClick={() => selectVocabularyWord(vocabularyWordOption.id)}
                >
                    <Stack key={vocabularyWordOption.id} direction="row" alignItems="center" sx={{ maxWidth: "100%" }}>
                        <Radio value={vocabularyWordOption.id} id={optionId} sx={{ flexShrink: 0 }} />
                        <TeacherAudioActions
                            context={buildVocabularyAudioReportContext(vocabularyWordOption)}
                            onPlay={() => selectVocabularyWord(vocabularyWordOption.id, true)}
                            speakerAriaLabel={`Play audio for ${vocabularyWordOption.word || vocabularyWordOption.audio_sentence || 'answer choice'}`}
                        />
                        <Stack direction="column" alignItems="center">
                            {vocabularyWordOption.word ?
                                <Typography sx={{ fontFamily: 'KaiTi', fontSize: '24px', overflowWrap: 'anywhere', textAlign: 'center' }}>
                                    {vocabularyWordOption.word}
                                </Typography>
                                :
                                <>{/* create empty child so foundAnswer.props.children[0] works */}</>
                            }
                            {vocabularyWordOption.image_url ?
                                <img
                                    src={vocabularyWordOption.image_url}
                                    alt={vocabularyWordOption.word}
                                    key={vocabularyWordOption.id}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setPreviewIndex(index);
                                    }}
                                    style={{ maxHeight: '125px', maxWidth: '125px', height: 'auto', width: 'auto', cursor: 'pointer', borderRadius: '12px', transition: 'transform 0.2s ease, filter 0.2s ease', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))' }}
                                    onMouseOver={(e) => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.08)'; (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 4px 16px rgba(0,0,0,0.12))'; }}
                                    onMouseOut={(e) => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLImageElement).style.filter = 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))'; }}
                                />
                                :
                                <>{/* create empty child so foundAnswer.props.children[0] works */}</>
                            }
                        </Stack>
                    </Stack>
                </Box>
                );
            });

            const filteredAnswerList = wordAnswerList.filter(Boolean);
            setAnswerList(filteredAnswerList);
            // use ref to avoid handleAnswerClick -> answerList dep cycle re-triggering this effect
            setActionList([renderPromptInputComponent(undefined, filteredAnswerList, handleAnswerClickRef.current)])
        }

    }, [
        chatBubbleDone, dialogPrompt.image_src, dialogPrompt.image_src_url,
        dialogPrompt.id, dialogPrompt.prompt, dialogPrompt.teacher_chinese_prompt,
        dialogPrompt.incorrect_vocabulary_word_list, dialogPrompt.correct_vocabulary_word,
        shouldUseTeacherChinesePrompt, teacherChinesePromptAudio, showAvatar, chatBubbleContainerRef,
        setActionList, selectVocabularyWord, renderPromptInputComponent, buildVocabularyAudioReportContext,
    ])

    React.useEffect(() => {
        // use ref so this effect doesn't re-run every time handleAnswerClick recreates (answerList dep)
        setActionList([renderPromptInputComponent(vocabularyWordId, answerList, handleAnswerClickRef.current)])
    }, [vocabularyWordId, answerList, setActionList, renderPromptInputComponent]);

    React.useEffect(() => {
        if (chatBubbleCorrectDone) {
            setDialogPromptComplete(dialogPrompt.id)
        }
    }, [chatBubbleCorrectDone, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack spacing={2}>
            <Stack sx={{ marginleft: "16px" }}>
                <ChatBubble
                    isTeacher={dialogPrompt.is_teacher}
                    message={dialogPrompt.prompt}
                    messageAudio={promptAudio ? promptAudio : null}
                    showAvatar={showAvatar}
                    setChatBubbleDone={setChatBubbleDone}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    dialogPromptId={dialogPrompt.id}
                    audioRole="prompt"
                />
            </Stack>
            {componentList.map((component, index) => (
                <Box key={index} component="div">
                    {component}
                </Box>
            ))}
            <Popover
                open={infoOpen}
                anchorEl={infoAnchorEl}
                onClose={handleInfoClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2, maxWidth: '300px' }}>
                    <Typography variant="body2">{selectedInfoText}</Typography>
                </Box>
            </Popover>

            {/* Preview dialog for image answer options */}
            <Dialog
                open={previewIndex !== null}
                onClose={() => setPreviewIndex(null)}
                fullScreen
                PaperProps={{
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.96)', color: '#fff' }
                }}
            >
                <DialogContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        height: '100vh',
                        overflow: 'hidden',
                        p: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                        }}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <IconButton
                            aria-label="Previous image"
                            onClick={handlePrev}
                            sx={{
                                position: 'absolute', left: 16,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.35)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
                                width: 48, height: 48,
                            }}
                        >
                            ‹
                        </IconButton>

                        {previewVocabularyWord && (
                            <img
                                src={previewVocabularyWord.image_url || 'broken'}
                                alt={previewVocabularyWord.word || ''}
                                style={{
                                    maxWidth: '95vw', maxHeight: '85vh',
                                    width: 'auto', height: 'auto',
                                    objectFit: 'contain',
                                    borderRadius: '14px',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
                                    display: 'block',
                                }}
                            />
                        )}

                        <IconButton
                            aria-label="Next image"
                            onClick={handleNext}
                            sx={{
                                position: 'absolute', right: 16,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.35)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
                                width: 48, height: 48,
                            }}
                        >
                            ›
                        </IconButton>

                        {previewIndex !== null && (
                            <Box
                                sx={{
                                    position: 'absolute', bottom: 16,
                                    left: '50%', transform: 'translateX(-50%)',
                                    px: 2, py: 0.5,
                                    borderRadius: '999px',
                                    backgroundColor: 'rgba(0,0,0,0.45)',
                                    color: '#ddd', fontSize: '13px',
                                    backdropFilter: 'blur(8px)',
                                }}
                            >
                                {previewIndex + 1} / {shuffledVocabWordList.length}
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setPreviewIndex(null)} sx={{ color: '#ccc' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (previewVocabularyWord) {
                                setVocabularyWordId(previewVocabularyWord.id);
                                vocabularyWordIdRef.current = previewVocabularyWord.id;
                                handleAnswerClick();
                                setPreviewIndex(null);
                            }
                        }}
                        className="secondaryButton"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default DialogPromptQuizQuestionAudioComponent;
